import { isPlatformBrowser } from '@angular/common';
import {
  FcmRegistrationError,
  FcmRegistrationErrorCategory,
} from './models/fcm-registration-state.model';

const FCM_REGISTRATION_META_KEY = 'fibo_fcm_registration';

export interface StoredFcmRegistrationMeta {
  fingerprint: string;
  registeredAt: string;
  userId: number;
}

export function buildFcmRegistrationFingerprint(
  userId: number | string,
  deviceId: string,
  fcmToken: string,
): string {
  return `${userId}:${deviceId}:${fcmToken}`;
}

export function getStoredFcmRegistrationMeta(platformId: object): StoredFcmRegistrationMeta | null {
  if (!isPlatformBrowser(platformId)) {
    return null;
  }

  try {
    const raw = localStorage.getItem(FCM_REGISTRATION_META_KEY);
    if (!raw) {
      return null;
    }

    if (raw.startsWith('{')) {
      const parsed = JSON.parse(raw) as StoredFcmRegistrationMeta;
      if (parsed?.fingerprint && parsed?.registeredAt && parsed?.userId) {
        return parsed;
      }
      return null;
    }

    // Legacy fingerprint-only value (no timestamp).
    return { fingerprint: raw, registeredAt: '', userId: 0 };
  } catch {
    return null;
  }
}

export function getStoredFcmRegistrationFingerprint(platformId: object): string | null {
  return getStoredFcmRegistrationMeta(platformId)?.fingerprint ?? null;
}

export function storeFcmRegistrationMeta(
  platformId: object,
  meta: StoredFcmRegistrationMeta,
): void {
  if (!isPlatformBrowser(platformId)) {
    return;
  }

  try {
    localStorage.setItem(FCM_REGISTRATION_META_KEY, JSON.stringify(meta));
  } catch (error) {
    console.warn('[FCM] Unable to persist registration metadata.', error);
  }
}

export function clearFcmRegistrationMeta(platformId: object): void {
  if (!isPlatformBrowser(platformId)) {
    return;
  }

  try {
    localStorage.removeItem(FCM_REGISTRATION_META_KEY);
  } catch (error) {
    console.warn('[FCM] Unable to clear registration metadata.', error);
  }
}

const TRANSIENT_HTTP_STATUSES = new Set([408, 429, 502, 503, 504]);

export function parseFcmRegistrationError(error: unknown): FcmRegistrationError {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? Number((error as { status: unknown }).status)
      : undefined;

  const message = extractErrorMessage(error);
  const category = categorizeHttpError(status, message);
  const retryable = category === 'network' || category === 'transient';

  return { message, category, status, retryable };
}

export function isRetryableRegistrationError(category: FcmRegistrationErrorCategory): boolean {
  return category === 'network' || category === 'transient';
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return 'Token registration failed.';
}

function categorizeHttpError(
  status: number | undefined,
  message: string,
): FcmRegistrationErrorCategory {
  if (status === 0 || status === undefined) {
    return 'network';
  }

  if (TRANSIENT_HTTP_STATUSES.has(status)) {
    return 'transient';
  }

  if (status === 401 || status === 403) {
    return 'auth';
  }

  if (status === 404) {
    return 'not_found';
  }

  if (status === 422) {
    return 'validation';
  }

  if (status >= 500) {
    return 'server';
  }

  if (status >= 400) {
    return 'client';
  }

  const lower = message.toLowerCase();
  if (lower.includes('network') || lower.includes('connection')) {
    return 'network';
  }

  return 'unknown';
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
